// Avisa al vendedor lo que la reevaluación soltó y, al dejar SureKeep, el código que se quitó.
import { toast } from 'sonner';
import type { AppliedSelection } from '../lib/applySelection';
import {
    droppedCodeCopy,
    droppedPromotionCopy,
    rejectionCopy,
    restoreDroppedCopy,
} from '../lib/promotionErrorCopy';
import { rememberedPromotionName, rememberedSureKeepOnly } from '../lib/promotionNames';
import type { SureKeepReleased } from '../lib/sureKeepRelease';

function lowerFirst(text: string): string {
    return text.charAt(0).toLocaleLowerCase('es-MX') + text.slice(1);
}

export function notifyDroppedPromotions(
    sessionId: string,
    applied: AppliedSelection | null,
    {
        withoutSureKeep = false,
        restored,
        promotionsAsNotices = false,
    }: {
        withoutSureKeep?: boolean;
        /** Las promos soltadas se avisan en pantalla, no por toast; lo regresado sigue por toast. */
        promotionsAsNotices?: boolean;
        /** Lo soltado de contado que se intentó regresar al volver a SureKeep. */
        restored?: SureKeepReleased;
    } = {},
): void {
    const restoredIds = new Set(restored?.choices.map((c) => c.choice.promotionId));
    const nameOf = (id: string) => rememberedPromotionName(sessionId, id) ?? 'una promo';
    for (const promotionId of applied?.droppedPromotionIds ?? []) {
        if (restoredIds.has(promotionId)) {
            toast.info(restoreDroppedCopy(nameOf(promotionId)), {
                id: `promotion-dropped-${promotionId}`,
            });
            continue;
        }
        if (promotionsAsNotices) continue;
        const reason =
            withoutSureKeep && rememberedSureKeepOnly(sessionId, promotionId)
                ? 'sureKeepOnly'
                : 'unavailable';
        toast.info(droppedPromotionCopy(rememberedPromotionName(sessionId, promotionId), reason), {
            id: `promotion-dropped-${promotionId}`,
        });
    }
    for (const drop of applied?.blockedDrops ?? []) {
        if (promotionsAsNotices && !restoredIds.has(drop.promotionId)) continue;
        const name = nameOf(drop.promotionId);
        const text = restoredIds.has(drop.promotionId)
            ? restoreDroppedCopy(name, `${drop.reason}.`)
            : `Quitamos ${name} de la venta: ${lowerFirst(drop.reason)}.`;
        toast.info(text, { id: `promotion-dropped-${drop.promotionId}` });
    }
    // El código se ve en «Arma la venta»; fuera de ahí se avisa aquí, con su motivo tal cual.
    const code = applied?.droppedCode;
    if (code && restored?.code?.code === code.code) {
        toast.info(
            restoreDroppedCopy(`el código ${code.code}`, rejectionCopy(code.rejectionCode)),
            {
                id: `code-dropped-${code.code}`,
            },
        );
    } else if (withoutSureKeep && code) {
        toast.info(droppedCodeCopy(code), { id: `code-dropped-${code.code}` });
    }
}
